import { AuthApi } from '@/service/authApi';
import { generateFingerPrint } from '@/service/utils';

const PROACTIVE_REFRESH_MS = 2 * 60 * 1000;

let refreshPromise = null;

/**
 * Parse expires_at from backend without blindly appending "Z".
 * Accepts ISO with timezone (Z / +00:00) or naive UTC timestamps.
 */
export function parseExpiresAt(expiresAt) {
    if (!expiresAt) {
        return null;
    }

    const value = String(expiresAt).trim();
    const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);
    const date = new Date(hasTimezone ? value : `${value}Z`);

    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return date;
}

export function hasAccessToken() {
    return Boolean(localStorage.getItem('accessToken'));
}

/**
 * Single-flight refresh: concurrent callers share one /auth/tokens/refresh.
 */
export async function refreshAccessToken() {
    if (refreshPromise) {
        return refreshPromise;
    }

    refreshPromise = (async () => {
        try {
            const fingerprint = await generateFingerPrint();
            const result = await AuthApi.refreshToken(fingerprint);

            if (result?.status !== 'success' || !hasAccessToken()) {
                return {
                    status: 'error',
                    code: result?.code || null,
                };
            }

            return { status: 'success' };
        } catch (error) {
            return {
                status: 'error',
                code: error?.response?.status || null,
                error,
            };
        } finally {
            refreshPromise = null;
        }
    })();

    return refreshPromise;
}

export function shouldRefreshAccessToken(skewMs = PROACTIVE_REFRESH_MS) {
    if (!hasAccessToken()) {
        return true;
    }

    const expiresAt = parseExpiresAt(localStorage.getItem('expiresAt'));
    if (!expiresAt) {
        return false;
    }

    return expiresAt.getTime() - Date.now() < skewMs;
}

/**
 * Refresh if token is missing or close to expiry.
 */
export async function ensureFreshAccessToken(skewMs = PROACTIVE_REFRESH_MS, options = {}) {
    const force = Boolean(options.force);

    if (!force && !shouldRefreshAccessToken(skewMs)) {
        return { status: 'success' };
    }

    return refreshAccessToken();
}

/**
 * Soft proactive refresh (timer / visibility). Never forces logout.
 */
export async function silentRefreshIfNeeded() {
    if (!hasAccessToken()) {
        return;
    }

    if (!shouldRefreshAccessToken(PROACTIVE_REFRESH_MS)) {
        return;
    }

    await refreshAccessToken();
}

export function startAuthSessionWatchers() {
    if (typeof window === 'undefined') {
        return;
    }

    const onVisible = () => {
        if (document.visibilityState === 'visible') {
            silentRefreshIfNeeded();
        }
    };

    document.addEventListener('visibilitychange', onVisible);
    window.setInterval(() => {
        silentRefreshIfNeeded();
    }, 60 * 1000);
}
